import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectRmEndDateTime,
  selectRmStartDateTime,
  selectSlowdownThreshold,
  selectTargetDate,
  selectTargetEndDate,
} from 'state/workflowSlice';

import { XyArray } from 'appTypes';
import dayjs from 'dayjs';
import Duration from 'dayjs/plugin/duration';
import { formatTraveltime } from 'features/common/utils';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';
import {
  useFromToDateTravelTimeData,
  useTargetDateMetrics,
} from 'features/workflow_traveltime/nrtTravelTimeCommon';
import Highcharts from 'highcharts';
import merge from 'lodash/merge';

import { isMultiDayChart } from './ChartDataExpander';
import { SyncedHighchartsReact } from './SyncedHighchartsReact';
import { TravelTimeChartDataExpander } from './TravelTimeChartDataExpander';
import { ChartVariant } from './chartOptions';
import {
  SeriesGroupType,
  dayMarkerLineBase,
  formatTargetSeriesName,
  formatTypicalSeriesName,
  makeFormatTooltip,
} from './nrtChartCommon';
import {
  freeflowSeriesOptions,
  travelTimeChartOptions,
} from './travelTimeChartOptions';
import { useChartResize } from './useChartResize';

import styles from './Chart.module.css';

dayjs.extend(Duration);

export function TravelTimeChart({
  variant = ChartVariant.TraveltimesWorkflow,
  additionalChartOptions = {},
  useDateRange = false,
  pollingInterval = 0,
}: {
  additionalChartOptions?: {};
  variant?: ChartVariant;
  useDateRange?: boolean;
  pollingInterval?: number;
}) {
  const targetDate: string = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const comparison = useAppSelector(selectComparison);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const startDateTime = useAppSelector(selectRmStartDateTime);
  const endDateTime = useAppSelector(selectRmEndDateTime);
  const slowdownThreshold = useAppSelector(selectSlowdownThreshold);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const chartComponent = useRef(null); // so we can call reflow
  const initialOptions = merge(
    {},
    travelTimeChartOptions,
    additionalChartOptions,
  ) as any;
  const [chartOptions, setChartOptions] = useState(initialOptions);

  const date = useDateRange
    ? {
        start: startDateTime,
        end: endDateTime,
      }
    : {
        start: targetDate,
        end: targetEndDate,
      };

  // ttData from REST api
  const { metrics, comparisonData: conventionalComparisonData } =
    useTargetDateMetrics(useDateRange, pollingInterval);
  const { nrt, typical } = useFromToDateTravelTimeData(!useDateRange);
  useChartResize(chartComponent);
  const data = useDateRange ? nrt : metrics;
  const comparisonData = useDateRange ? typical : conventionalComparisonData;

  // memoize chartDataExpander
  const chartDataExpander = useMemo(() => {
    if (!date.start) {
      return undefined;
    }
    return new TravelTimeChartDataExpander(
      date.start,
      undefined,
      date.end,
      comparisonEndDate,
    );
  }, [date.start, date.end, comparisonEndDate]);

  // memoize expanded segment travel times
  const { goodTargetTTs, questionableTargetTTs, unfilteredTargetDense } =
    useMemo(() => {
      if (!data || !chartDataExpander) return {};
      const { goodDense, questionableDense, rawDense } =
        chartDataExpander.expandQuestionableTraveltimes(data);
      return {
        questionableTargetTTs: questionableDense,
        goodTargetTTs: goodDense,
        unfilteredTargetDense: rawDense,
      };
    }, [data, chartDataExpander]);
  // console.log(`memoized segmentSpeeds ${JSON.stringify(segmentSpeeds)}`);

  // memoize typicalSpeeds
  const {
    goodComparisonTTs,
    questionableComparisonTTs,
    unfilteredComparisonDense,
  } = useMemo(() => {
    if (comparison) {
      const comparisonChartDataExpander = new TravelTimeChartDataExpander(
        date.start,
        comparison,
        date.end,
        comparisonEndDate,
      );
      if (!comparisonData || !date.start || !comparison) return {};
      const { goodDense, questionableDense, rawDense } =
        comparisonChartDataExpander.expandQuestionableTraveltimes(
          comparisonData,
        );
      return {
        questionableComparisonTTs: questionableDense,
        goodComparisonTTs: goodDense,
        unfilteredComparisonDense: rawDense,
      };
    } else {
      return {};
    }
  }, [comparisonData, date.start, date.end, comparison, comparisonEndDate]);

  // memoize thresholds
  const slowdownBands = useMemo(
    () =>
      chartDataExpander?.slowdownBands(
        unfilteredTargetDense,
        unfilteredComparisonDense,
        1 + slowdownThreshold,
        true,
      ),
    [
      unfilteredTargetDense,
      unfilteredComparisonDense,
      slowdownThreshold,
      chartDataExpander,
    ],
  );

  // update chart with new startDate, freeflowSpeeds, segmentSpeeds
  useEffect(() => {
    const typical_series_name = formatTypicalSeriesName(
      comparison,
      targetDate,
      userProject?.uses_middle_east_day_types,
      variant === ChartVariant.RouteMonitoring,
      undefined,
      false,
      comparisonEndDate,
    );

    const target_series_name = formatTargetSeriesName(
      targetDate,
      variant === ChartVariant.RouteMonitoring,
      targetEndDate,
    );
    const serieses = [
      {
        id: 'typical_tt',
        name: typical_series_name,
        data: goodComparisonTTs || [],
        marker: { enabled: false },
      },
      {
        id: 'questionable_typical_tt',
        name: `Estimated ${typical_series_name}`,
        data: questionableComparisonTTs || [],
        marker: { enabled: false },
      },
      {
        id: 'segment_tt',
        data: goodTargetTTs || [],
        name: target_series_name,
        marker: { enabled: false },
      },
      {
        id: 'questionable_segment_tt',
        data: questionableTargetTTs || [],
        name: `Estimated ${target_series_name}`,
        marker: { enabled: false },
      },
    ];
    let freeflow_flatline: XyArray;
    if (data?.freeflow_traveltime) {
      freeflow_flatline = data?.freeflow_traveltime
        ? chartDataExpander?.flatline(data.freeflow_traveltime)
        : [];
      serieses.push({
        ...freeflowSeriesOptions,
        id: 'freeflow',
        data: freeflow_flatline,
      } as any);
    }

    const nrtMultiDay = isMultiDayChart(
      targetDate,
      targetEndDate,
      comparison,
      comparisonEndDate,
    );
    const dayStart = dayjs(targetDate).startOf('day');
    const dayEnd = dayStart.add(1, 'day');
    const xAxisOptions: any = nrtMultiDay
      ? {
          plotBands: slowdownBands,
          min: dayStart.valueOf(),
          max: dayEnd.valueOf(),
          startOnTick: true,
          endOnTick: true,
        }
      : {
          plotBands: slowdownBands,
          min: null,
          max: null,
          plotLines: [
            { ...dayMarkerLineBase, value: dayjs(targetDate) },
            { ...dayMarkerLineBase, value: dayjs(targetDate).add(1, 'day') },
            { ...dayMarkerLineBase, value: dayjs(targetDate).add(2, 'day') }, // RM max case 30 hours
          ],
        };

    setChartOptions({
      xAxis: xAxisOptions,
      series: serieses,
      tooltip: {
        formatter: makeFormatTooltip(
          slowdownBands,
          data?.bin_size,
          userProject?.uses_middle_east_day_types,
          formatTraveltime,
          targetDate,
          comparison,
          {
            [SeriesGroupType.Nrt]: ['segment_tt', 'questionable_segment_tt'],
            [SeriesGroupType.Comparison]: [
              'typical_tt',
              'questionable_typical_tt',
            ],
          },
          data?.freeflow_traveltime,
          variant,
          targetEndDate,
          comparisonEndDate,
        ),
      },
    });
  }, [
    goodTargetTTs,
    goodComparisonTTs,
    questionableTargetTTs,
    questionableComparisonTTs,
    slowdownBands,
    data,
    targetDate,
    targetEndDate,
    comparison,
    comparisonEndDate,
  ]);

  const loading = !unfilteredTargetDense || !unfilteredComparisonDense;

  return (
    <div className={styles.chart} style={{ width: '100%' }}>
      {loading && (
        <div className={styles.loading_wrapper}>
          <LoadingDotAnimation />
        </div>
      )}
      <SyncedHighchartsReact
        ref={chartComponent}
        highcharts={Highcharts}
        containerProps={{
          style: {
            height: '100%',
            width: '100%',
            visibility: loading ? 'hidden' : 'unset',
          },
        }}
        options={chartOptions}
      />
    </div>
  );
}
